import { Send } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { cn } from '@/lib/utils';
import { formatTime } from '@/modules/debug/linkedin/format';
import {
  messageState,
  type MessageStateKind,
} from '@/modules/debug/linkedin/linkedInDebug';
import type {
  DebugLinkedInCandidateJob,
  DebugLinkedInMessage,
} from '@/modules/debug/linkedin/types';
import { Button } from '@/modules/ui/components/button/Button';
import Loader from '@/modules/ui/components/loader/Loader';

const STATE_LABELS: Record<MessageStateKind, string> = {
  scheduled: 'Scheduled for',
  queued: 'Queued',
  draft: 'Draft',
  failed: 'Failed',
  sent: 'Sent',
};

interface MessageBubbleProps {
  message: DebugLinkedInMessage;
  jobTitle: string | null;
  onSendNow: (messageId: string) => void;
  sendingNow: boolean;
}

function MessageBubble({
  message,
  jobTitle,
  onSendNow,
  sendingNow,
}: MessageBubbleProps) {
  const isRecruiter = message.author === 'Recruiter';
  const state = messageState(message);
  const isUnsent = state.kind !== 'sent';

  return (
    <div className={cn('flex', isRecruiter ? 'justify-end' : 'justify-start')}>
      <div className="flex max-w-[70%] flex-col items-end gap-1">
        <div
          className={cn(
            'w-full rounded-2xl px-4 py-2 text-sm shadow-sm',
            isUnsent
              ? 'bg-slate-200 text-slate-800'
              : isRecruiter
                ? 'bg-[#0a66c2] text-white'
                : 'bg-muted text-foreground',
            state.kind === 'failed' && 'border border-red-400',
          )}
        >
          <p className="whitespace-pre-wrap">{message.content}</p>
          <p
            className={cn(
              'mt-1 flex flex-wrap gap-x-2 text-[10px]',
              isUnsent
                ? 'text-slate-600'
                : isRecruiter
                  ? 'text-white/70'
                  : 'text-muted-foreground',
            )}
          >
            <span className={cn(isUnsent && 'font-semibold')}>
              {STATE_LABELS[state.kind]} {formatTime(state.at)}
            </span>
            {jobTitle && <span>· {jobTitle}</span>}
            {message.externalMessageId && (
              <span className="font-mono">{message.externalMessageId}</span>
            )}
          </p>
          {message.error && (
            <p className="mt-1 text-[10px] font-medium">{message.error}</p>
          )}
        </div>
        {state.kind === 'scheduled' && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onSendNow(message.id)}
            loading={sendingNow}
          >
            <Send className="mr-1 size-3" />
            Send now
          </Button>
        )}
      </div>
    </div>
  );
}

interface MessageListProps {
  messages: DebugLinkedInMessage[];
  candidateJobs: DebugLinkedInCandidateJob[];
  loading: boolean;
  autoReplyNote: string | null;
  onSendNow: (messageId: string) => void;
  sendingNowId: string | null;
}

export function MessageList({
  messages,
  candidateJobs,
  loading,
  autoReplyNote,
  onSendNow,
  sendingNowId,
}: MessageListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  if (loading && messages.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
        No messages in this thread yet.
      </div>
    );
  }

  // Tag messages with their job only when the thread spans several jobs.
  const jobTitles =
    candidateJobs.length > 1
      ? new Map(candidateJobs.map((job) => [job.candidateJobId, job.jobTitle]))
      : null;

  return (
    <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-6 py-4">
      {messages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
          jobTitle={
            (message.candidateJobId &&
              jobTitles?.get(message.candidateJobId)) ||
            null
          }
          onSendNow={onSendNow}
          sendingNow={sendingNowId === message.id}
        />
      ))}
      {autoReplyNote && (
        <p className="text-muted-foreground text-center text-xs italic">
          {autoReplyNote}
        </p>
      )}
    </div>
  );
}
